import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Refund, RefundCreateData, RefundDocumentRequest, RefundFileChangeResponse, RefundRequest, RefundSliceModel, RefundTableResponse, UploadRefundFilesRequest } from "./refund-interfaces";
import { ErrorResponse, ListModel, MainResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { showNotification } from "shared/slices/statusNotificationsSlice";
import { NotificationTypes } from "shared/interfaces/status-notifications-interfaces";
import { API_BASE_URL as baseUri } from "config/env";

export const GetAllRefunds = createAsyncThunk<RefundTableResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/RefundSlice/GetAllRefunds',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/refunds/all', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as RefundTableResponse[];
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetMyRefunds = createAsyncThunk<RefundTableResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/RefundSlice/GetMyRefunds',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/refunds/get-my', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as RefundTableResponse[];
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetAllInactiveRefunds = createAsyncThunk<RefundTableResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/RefundSlice/GetAllInactiveRefunds',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/refunds/allInactive', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as RefundTableResponse[];
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetRefundCreateData = createAsyncThunk<RefundCreateData, void, { rejectValue: RejectedValue }>(
    'Slice/RefundSlice/GetRefundCreateData',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/refunds/get-create-data', {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getModel = await response.json() as RefundCreateData;
            return getModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetRefund = createAsyncThunk<Refund, string, { rejectValue: RejectedValue }>(
    'Slice/RefundSlice/GetRefund',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/refunds/get?id=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getModel = await response.json() as Refund;
            return getModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateRefund = createAsyncThunk<MainResponse, RefundRequest, { rejectValue: RejectedValue }>(
    'Slice/RefundSlice/CreateRefund',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/refunds/create', {
            method: "POST",
            headers: AuthHeader(true, true),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetAllRefunds());
            dispatch(GetMyRefunds());
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Refund created successfully.", type: NotificationTypes.Success }));
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateRefund = createAsyncThunk<MainResponse, RefundRequest, { rejectValue: RejectedValue }>(
    'Slice/RefundSlice/UpdateRefund',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/refunds/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetAllRefunds());
            dispatch(GetMyRefunds());
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Refund updated successfully.", type: NotificationTypes.Success }));
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateRefund = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/RefundSlice/DeactivateRefund',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/refunds/deactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            dispatch(GetAllRefunds());
            dispatch(GetMyRefunds());
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Refund deactivated successfully.", type: NotificationTypes.Success }));
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UploadRefundFiles = createAsyncThunk<RefundFileChangeResponse, UploadRefundFilesRequest, { rejectValue: RejectedValue }>(
    'Slice/RefundSlice/UploadRefundFiles',
    async (request: UploadRefundFilesRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/refunds/upload-files', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as RefundFileChangeResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Files uploaded successfully.", type: NotificationTypes.Success }));
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteRefundFile = createAsyncThunk<RefundFileChangeResponse, { refundId: string; fileId: string }, { rejectValue: RejectedValue }>(
    'Slice/RefundSlice/DeleteRefundFile',
    async ({ refundId, fileId }, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + `api/refunds/delete-file?refundId=${refundId}&id=${fileId}`, {
            method: "DELETE",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as RefundFileChangeResponse;
            dispatch(showNotification({ message: responseModel.message ?? "File deleted successfully.", type: NotificationTypes.Success }));
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivateRefund = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/RefundSlice/ReactivateRefund',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/refunds/reactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            dispatch(GetAllInactiveRefunds());
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Refund reactivated successfully.", type: NotificationTypes.Success }));
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DownloadRefundFile = createAsyncThunk<RefundDocumentRequest, { refundId: string; fileId: string }, { rejectValue: RejectedValue }>(
    'Slice/RefundSlice/DownloadRefundFile',
    async ({ refundId, fileId }, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/refunds/download-file?id=${refundId}&fileId=${fileId}`, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as RefundDocumentRequest;
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetRefundFiles = createAsyncThunk<RefundDocumentRequest[], string, { rejectValue: RejectedValue }>(
    'Slice/RefundSlice/GetRefundFiles',
    async (refundId: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/refunds/get-files?id=' + refundId, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as RefundDocumentRequest[];
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateSupplierFromDropdown = createAsyncThunk<ListModel<string>[], string, { rejectValue: RejectedValue }>(
    'Slice/RefundSlice/CreateSupplierFromDropdown',
    async (name: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/suppliers/create-from-dropdown?name=' + encodeURIComponent(name), {
            method: "POST",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ListModel<string>[];
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

const initialValue: RefundSliceModel = {
    statusCode: '',
    isLoading: false,
    isModalLoading: false,
    isSupplierDropdownLoading: false,
    message: '',
    showNotification: false,
    listOfAllRefunds: [],
    listOfAllInactiveRefunds: [],
    listOfMyRefunds: [],
    reloadData: false,
    refund: null,
    refundCreateData: null,
    permissions: {
        createEditTenants: [],
        reactivateDeactivateTenants: [],
        deleteTenants: []
    }
}

export const RefundSlice = createSlice({
    name: 'RefundSlice',
    initialState: initialValue,
    reducers: {
        resetRefundSlice: (state) => {
            state.statusCode = '';
            state.isLoading = false;
            state.isModalLoading = false;
            state.isSupplierDropdownLoading = false;
            state.reloadData = false;
            state.showNotification = false;
            state.message = '';
            state.listOfAllRefunds = [];
            state.listOfAllInactiveRefunds = [];
            state.listOfMyRefunds = [];
            state.refund = null;
            state.refundCreateData = null;
        },
        resetRefund: (state) => {
            state.refund = null;
            state.isModalLoading = false;
            state.reloadData = false;
        },
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
            state.reloadData = false;
        }
    },
    extraReducers(builder) {
        //GetAllRefunds
        builder
            .addCase(GetAllRefunds.fulfilled, (state, action: PayloadAction<RefundTableResponse[]>) => {
                state.isLoading = false;
                state.message = '';
                state.listOfAllRefunds = action.payload;
            })
            .addCase(GetAllRefunds.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetAllRefunds.pending, state => {
                state.isLoading = true;
            })
        //GetMyRefunds
        builder
            .addCase(GetMyRefunds.fulfilled, (state, action: PayloadAction<RefundTableResponse[]>) => {
                state.isLoading = false;
                state.listOfMyRefunds = action.payload;
            })
            .addCase(GetMyRefunds.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetMyRefunds.pending, state => {
                state.isLoading = true;
            })
        //GetAllInactiveRefunds
        builder
            .addCase(GetAllInactiveRefunds.fulfilled, (state, action: PayloadAction<RefundTableResponse[]>) => {
                state.isLoading = false;
                state.listOfAllInactiveRefunds = action.payload;
            })
            .addCase(GetAllInactiveRefunds.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetAllInactiveRefunds.pending, state => {
                state.isLoading = true;
            })
        //GetRefund
        builder
            .addCase(GetRefund.fulfilled, (state, action: PayloadAction<Refund>) => {
                state.isModalLoading = false;
                state.message = '';
                state.refund = action.payload;
            })
            .addCase(GetRefund.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetRefund.pending, state => {
                state.isModalLoading = true;
            })
        //GetRefundCreateData
        builder
            .addCase(GetRefundCreateData.fulfilled, (state, action: PayloadAction<RefundCreateData>) => {
                state.isModalLoading = false;
                state.message = '';
                state.refundCreateData = action.payload;
            })
            .addCase(GetRefundCreateData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetRefundCreateData.pending, state => {
                state.isModalLoading = true;
            })
        //CreateRefund
        builder
            .addCase(CreateRefund.fulfilled, (state) => {
                state.isModalLoading = false;
                state.reloadData = true;
            })
            .addCase(CreateRefund.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateRefund.pending, state => {
                state.isModalLoading = true;
            })
        //UpdateRefund
        builder
            .addCase(UpdateRefund.fulfilled, (state) => {
                state.isModalLoading = false;
                state.reloadData = true;
            })
            .addCase(UpdateRefund.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateRefund.pending, state => {
                state.isModalLoading = true;
            })
        //DeactivateRefund
        builder
            .addCase(DeactivateRefund.fulfilled, (state) => {
                state.isLoading = false;
                state.reloadData = true;
            })
            .addCase(DeactivateRefund.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivateRefund.pending, state => {
                state.isLoading = true;
            })
        //ReactivateRefund
        builder
            .addCase(ReactivateRefund.fulfilled, (state) => {
                state.isLoading = false;
                state.reloadData = true;
            })
            .addCase(ReactivateRefund.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(ReactivateRefund.pending, state => {
                state.isLoading = true;
            })
        //CreateSupplierFromDropdown
        builder
            .addCase(CreateSupplierFromDropdown.fulfilled, (state, action: PayloadAction<ListModel<string>[]>) => {
                state.isSupplierDropdownLoading = false;
                if (state.refundCreateData) {
                    state.refundCreateData.suppliers = action.payload;
                }
            })
            .addCase(CreateSupplierFromDropdown.rejected, (state) => {
                state.isSupplierDropdownLoading = false;
            })
            .addCase(CreateSupplierFromDropdown.pending, state => {
                state.isSupplierDropdownLoading = true;
            })
        //UploadRefundFiles
        builder
            .addCase(UploadRefundFiles.fulfilled, (state, action: PayloadAction<RefundFileChangeResponse>) => {
                state.message = action.payload.message ?? 'File uploaded successfully.';
                state.showNotification = true;
                if (state.refund) {
                    state.refund.files = action.payload.files;
                }
            })
            .addCase(UploadRefundFiles.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UploadRefundFiles.pending, (_state) => { })
        //DeleteRefundFile
        builder
            .addCase(DeleteRefundFile.fulfilled, (state, action: PayloadAction<RefundFileChangeResponse>) => {
                state.message = action.payload.message ?? 'File deleted successfully.';
                state.showNotification = true;
                if (state.refund) {
                    state.refund.files = action.payload.files;
                }
            })
            .addCase(DeleteRefundFile.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteRefundFile.pending, (_state) => { })
        //GetRefundFiles
        builder
            .addCase(GetRefundFiles.fulfilled, (state, action: PayloadAction<RefundDocumentRequest[]>) => {
                if (state.refund) {
                    state.refund.files = action.payload;
                }
            })
            .addCase(GetRefundFiles.rejected, (_state) => { })
            .addCase(GetRefundFiles.pending, (_state) => { })
    },
});

export const { resetRefund, resetRefundSlice, resetStatusCode } = RefundSlice.actions;
export default RefundSlice.reducer;
